import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Badge, Button, Dialog, Skeleton, useToast } from '../ui';
import api from '../config/api';
import { errorMessage } from '../errors';

const PREVIEW_FIELDS = 4;

/**
 * The item types of a collection and its default one. Only the collection
 * owner's types can be linked to it; an admin editing someone else's
 * collection is shown that owner's types.
 */
function EditInventoryItemTypesModal({ inventory, currentUserId, onClose, onUpdate }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [allItemTypes, setAllItemTypes] = useState([]);
  const [selectedItemTypes, setSelectedItemTypes] = useState(() => (inventory.item_types || []).map(it => it.id));
  const [defaultItemTypeId, setDefaultItemTypeId] = useState(() => {
    const types = inventory.item_types || [];
    const defaultType = types.find(it => it.is_default);
    return defaultType ? defaultType.id : (types[0]?.id ?? '');
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    // An admin editing someone else's inventory needs every account's types to find them.
    const othersInventory = inventory.owner_id !== currentUserId;
    api.get(othersInventory ? '/item-types?scope=all' : '/item-types')
      .then((response) => { if (alive) setAllItemTypes(response.data.filter(type => type.owner_id === inventory.owner_id)); })
      .catch((error) => {
        console.error('Error fetching item types:', error);
        if (alive) toast.error(t('editCollectionTypes.loadError'));
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [inventory.owner_id, currentUserId, toast, t]);

  const toggle = (itemTypeId) => {
    if (selectedItemTypes.includes(itemTypeId)) {
      const next = selectedItemTypes.filter(id => id !== itemTypeId);
      setSelectedItemTypes(next);
      // The default removed: the next selected one becomes the default.
      if (defaultItemTypeId === itemTypeId) setDefaultItemTypeId(next.length > 0 ? next[0] : '');
    } else {
      const next = [...selectedItemTypes, itemTypeId];
      setSelectedItemTypes(next);
      // The first one selected is the default.
      if (next.length === 1) setDefaultItemTypeId(itemTypeId);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (selectedItemTypes.length === 0) {
      toast.error(t('createCollection.selectOne'));
      return;
    }
    if (!defaultItemTypeId || !selectedItemTypes.includes(defaultItemTypeId)) {
      toast.error(t('editCollectionTypes.needDefault'));
      return;
    }
    setSaving(true);
    try {
      const response = await api.put(`/inventories/${inventory.id}/item-types`, {
        item_types: selectedItemTypes,
        default_item_type_id: defaultItemTypeId,
      });
      toast.success(t('editCollectionTypes.saved'));
      onUpdate(response.data);
      onClose();
    } catch (error) {
      console.error('Error updating item types:', error);
      toast.error(t('editCollectionTypes.saveError', { error: errorMessage(t, error) }));
    } finally {
      setSaving(false);
    }
  };

  const defaultItemType = allItemTypes.find(type => type.id === parseInt(defaultItemTypeId, 10));

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('editCollectionTypes.title', { name: inventory.name })}
      size="lg"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="collection-types" disabled={selectedItemTypes.length === 0 || loading} loading={saving}>
            {t('editCollectionTypes.save')}
          </Button>
        </>
      )}
    >
      <form id="collection-types" className="dialog-form" onSubmit={handleSave}>
        <div className="dialog-preview">
          <p className="dialog-preview__title">{t('editCollectionTypes.current')}</p>
          {(inventory.item_types || []).length > 0 ? (
            <ul className="tag-list">
              {inventory.item_types.map(itemType => (
                <li key={itemType.id} className="ui-chip ui-chip--static">
                  {itemType.name}
                  {itemType.is_default && <Badge tone="accent">{t('collections.default')}</Badge>}
                </li>
              ))}
            </ul>
          ) : (
            <p className="ui-field__hint">{t('editCollectionTypes.none')}</p>
          )}
        </div>
        <fieldset className="choice-list">
          <legend className="ui-field__label">{t('editCollectionTypes.select')}</legend>
          {loading ? (
            <div className="choice-list__rows">{[0, 1, 2].map(i => <Skeleton key={i} height={52} />)}</div>
          ) : (
            <ul className="choice-list__rows">
              {allItemTypes.map(itemType => {
                const selected = selectedItemTypes.includes(itemType.id);
                return (
                  <li key={itemType.id} className={`choice-row${selected ? ' is-selected' : ''}`}>
                    <input type="checkbox" id={`itemtype-${itemType.id}`} checked={selected} onChange={() => toggle(itemType.id)} />
                    <label htmlFor={`itemtype-${itemType.id}`} className="choice-row__text">
                      <span className="choice-row__title">{itemType.name}</span>
                      {itemType.description && <span className="choice-row__hint">{itemType.description}</span>}
                      <span className="choice-row__hint">{t('collections.fieldCount', { count: itemType.fields?.length || 0 })}</span>
                    </label>
                    {selected && (
                      <label className="choice-row__default">
                        <input type="radio" name="defaultItemType" checked={defaultItemTypeId === itemType.id}
                               onChange={() => setDefaultItemTypeId(itemType.id)} />
                        {t('collections.default')}
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <p className={`ui-field__hint${!loading && selectedItemTypes.length === 0 ? ' is-warning' : ''}`} aria-live="polite">
            {selectedItemTypes.length === 0
              ? t('createCollection.selectOne')
              : [t('createCollection.selected', { count: selectedItemTypes.length }),
                defaultItemType ? t('createCollection.defaultIs', { name: defaultItemType.name }) : null].filter(Boolean).join(' · ')}
          </p>
        </fieldset>

        {defaultItemType && (
          <div className="dialog-preview">
            <p className="dialog-preview__title">
              {t('createCollection.fieldsByDefault')} <Badge tone="accent">{defaultItemType.name}</Badge>
            </p>
            {(defaultItemType.fields || []).length > 0 ? (
              <ul className="tag-list">
                {defaultItemType.fields.slice(0, PREVIEW_FIELDS).map(field => (
                  <li key={field.key || field.name} className="ui-chip ui-chip--static">
                    {field.name}{field.required && <span aria-hidden="true"> *</span>}
                  </li>
                ))}
                {defaultItemType.fields.length > PREVIEW_FIELDS && (
                  <li className="tag-list__more">{t('createCollection.moreFields', { count: defaultItemType.fields.length - PREVIEW_FIELDS })}</li>
                )}
              </ul>
            ) : (
              <p className="ui-field__hint">{t('createCollection.noFields')}</p>
            )}
          </div>
        )}

        <Alert tone="info" title={t('editCollectionTypes.important')}>
          <ul className="alert-list">
            <li>{t('editCollectionTypes.note1')}</li>
            <li>{t('editCollectionTypes.note2')}</li>
            <li>{t('editCollectionTypes.note3')}</li>
          </ul>
        </Alert>
      </form>
    </Dialog>
  );
}

export default EditInventoryItemTypesModal;
